'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { OTPInput } from '@/components/auth/OTPInput';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { validatePassword } from '@/lib/utils/password';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { setAuthToken } from '@/lib/auth/token';
import { cn } from '@/lib/utils/cn';

export type AuthMode =
  | 'login'
  | 'register'
  | 'verify-email'
  | 'forgot-password'
  | 'verify-reset'
  | 'reset-password';

interface AuthFlowProps {
  initialMode?: AuthMode;
  initialEmail?: string;
  onSuccess?: () => void;
  className?: string;
  compact?: boolean;
  initialAccountType?: 'smb' | 'agency' | 'creator';
}

type AuthPayload = {
  error?: string;
  message?: string;
  accessToken?: string;
  requiresVerification?: boolean;
  authProvider?: 'email' | 'google' | 'apple';
  googleSignIn?: boolean;
  appleSignIn?: boolean;
};

/** Аккаунт без пароля: сервер отвечает 409 и говорит, через какой OAuth входить. */
function oauthProviderOf(data: AuthPayload): 'google' | 'apple' | null {
  if (data.appleSignIn || data.authProvider === 'apple') return 'apple';
  if (data.googleSignIn || data.authProvider === 'google') return 'google';
  return null;
}

function AppleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M16.365 12.77c-.02-2.19 1.79-3.24 1.87-3.29-1.02-1.49-2.61-1.7-3.17-1.72-1.35-.14-2.63.79-3.32.79-.68 0-1.74-.77-2.86-.75-1.47.02-2.83.86-3.59 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.87.69 1.19-.02 1.94-1.08 2.66-2.14.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.32-.89-2.36-3.53zM14.19 6.34c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.69-.92 2.68.97.08 1.96-.49 2.56-1.23z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function AuthFlow({
  initialMode = 'login',
  initialEmail = '',
  onSuccess,
  className,
  compact = false,
  initialAccountType = 'smb',
}: AuthFlowProps) {
  const router = useRouter();
  const { checkAuth } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [formData, setFormData] = useState({
    email: initialEmail,
    password: '',
    name: '',
    accountType: initialAccountType as 'smb' | 'agency' | 'creator',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = window.setTimeout(() => setResendTimer((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendTimer]);

  const copy = {
    login: {
      title: t('auth.login.title'),
      subtitle: t('auth.login.subtitle'),
      cta: t('auth.login.cta'),
    },
    register: {
      title: t('auth.register.title'),
      subtitle: t('auth.register.subtitle'),
      cta: t('auth.register.cta'),
    },
    'verify-email': {
      title: t('auth.verifyEmail.title'),
      subtitle: t('auth.verifyEmail.subtitle', { email: formData.email || t('auth.yourInbox') }),
      cta: t('auth.verifyEmail.cta'),
    },
    'forgot-password': {
      title: t('auth.forgot.title'),
      subtitle: t('auth.forgot.subtitle'),
      cta: t('auth.forgot.cta'),
    },
    'verify-reset': {
      title: t('auth.verifyReset.title'),
      subtitle: t('auth.verifyReset.subtitle', { email: formData.email || t('auth.yourInbox') }),
      cta: t('auth.verifyReset.cta'),
    },
    'reset-password': {
      title: t('auth.reset.title'),
      subtitle: t('auth.reset.subtitle'),
      cta: t('auth.reset.cta'),
    },
  }[mode];

  const setField = (key: keyof typeof formData, value: string) => {
    setFormData((current) => ({ ...current, [key]: value }));
  };

  const goOAuth = (provider: 'google' | 'apple') => {
    // На регистрации тип выбран над кнопками. На входе его не спрашиваем:
    // если аккаунта ещё нет, колбэк создаст его без типа и приведёт на экран
    // выбора — раньше такой аккаунт молча становился smb.
    const accountType = mode === 'register' ? `&accountType=${formData.accountType}` : '';
    window.location.href = `/api/auth/${provider}?action=login${accountType}`;
  };
  const goGoogle = () => goOAuth('google');
  const goApple = () => goOAuth('apple');

  const completeAuth = async (accessToken: string) => {
    setAuthToken(accessToken);
    await checkAuth();
    if (onSuccess) {
      onSuccess();
    } else {
      const meResponse = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const me = meResponse.ok ? await meResponse.json() : null;
      if (me?.user?.accountTypePending) {
        router.push('/auth/account-type');
        return;
      }
      const kind = me?.user?.accountType;
      router.push(kind === 'creator' ? '/creator' : kind === 'viewer' ? '/brand' : '/main');
    }
  };

  const requestJson = async (url: string, body: Record<string, unknown>) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data: AuthPayload = await response.json();

    const provider = oauthProviderOf(data);
    if (provider) {
      goOAuth(provider);
      return { response, data, redirected: true };
    }

    return { response, data, redirected: false };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const { response, data, redirected } = await requestJson('/api/auth/login', {
          email: formData.email,
          password: formData.password,
        });
        if (redirected) return;
        if (!response.ok) {
          if (data.requiresVerification) {
            setMode('verify-email');
            setSuccess(t('auth.msg.verifyToContinue'));
            return;
          }
          throw new Error(data.error || t('auth.err.loginFailed'));
        }
        if (!data.accessToken) throw new Error(t('auth.err.missingToken'));
        await completeAuth(data.accessToken);
        return;
      }

      if (mode === 'register') {
        const passwordValidation = validatePassword(formData.password);
        if (!passwordValidation.isValid) {
          setError(t('auth.err.passwordRequirements'));
          return;
        }

        const { response, data, redirected } = await requestJson('/api/auth/register', {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          accountType: formData.accountType,
        });
        if (redirected) return;
        if (!response.ok) throw new Error(data.error || t('auth.err.registrationFailed'));
        setMode('verify-email');
        setSuccess(t('auth.msg.codeSent'));
        setResendTimer(60);
        return;
      }

      if (mode === 'verify-email') {
        if (formData.otp.length !== 6) {
          setError(t('auth.err.enterCode'));
          return;
        }
        const { response, data } = await requestJson('/api/auth/verify-email', {
          email: formData.email,
          code: formData.otp,
        });
        if (!response.ok) throw new Error(data.error || t('auth.err.verificationFailed'));
        if (!data.accessToken) throw new Error(t('auth.err.missingToken'));
        await completeAuth(data.accessToken);
        return;
      }

      if (mode === 'forgot-password') {
        const { response, data, redirected } = await requestJson('/api/auth/forgot-password', {
          email: formData.email,
        });
        if (redirected) return;
        if (!response.ok) throw new Error(data.error || t('auth.err.resetSendFailed'));
        setMode('verify-reset');
        setSuccess(t('auth.msg.resetCodeSent'));
        return;
      }

      if (mode === 'verify-reset') {
        if (formData.otp.length !== 6) {
          setError(t('auth.err.enterCode'));
          return;
        }
        setMode('reset-password');
        setSuccess(t('auth.msg.codeVerified'));
        return;
      }

      if (mode === 'reset-password') {
        if (formData.newPassword !== formData.confirmPassword) {
          setError(t('auth.err.passwordsMismatch'));
          return;
        }
        const passwordValidation = validatePassword(formData.newPassword);
        if (!passwordValidation.isValid) {
          setError(t('auth.err.passwordRequirements'));
          return;
        }
        const { response, data } = await requestJson('/api/auth/reset-password', {
          email: formData.email,
          code: formData.otp,
          newPassword: formData.newPassword,
        });
        if (!response.ok) throw new Error(data.error || t('auth.err.resetFailed'));
        setMode('login');
        setSuccess(t('auth.msg.passwordUpdated'));
        setFormData((current) => ({
          ...current,
          password: '',
          otp: '',
          newPassword: '',
          confirmPassword: '',
        }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.err.somethingWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { response, data } = await requestJson('/api/auth/resend-code', {
        email: formData.email,
      });
      if (!response.ok) {
        if (response.status === 429 && typeof (data as any).remainingTime === 'number') {
          setResendTimer((data as any).remainingTime);
        }
        throw new Error(data.error || t('auth.err.resetSendFailed'));
      }
      setSuccess(t('auth.msg.codeResent'));
      setResendTimer(60);
      setField('otp', '');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.err.somethingWrong'));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setError('');
    setSuccess('');
    if (mode === 'verify-email') setMode('register');
    else if (mode === 'forgot-password' || mode === 'verify-reset' || mode === 'reset-password') setMode('login');
  };

  const showBack = ['verify-email', 'forgot-password', 'verify-reset', 'reset-password'].includes(mode);
  const showOAuth = mode === 'login' || mode === 'register';

  return (
    <div className={cn('w-full text-ink-950', className)}>
      <div className="relative text-center">
        {showBack && (
          <button
            type="button"
            onClick={goBack}
            className="absolute left-0 top-1 flex h-9 w-9 items-center justify-center rounded-full border border-ink-950/10 text-ink-500 transition hover:bg-ink-950/5 hover:text-ink-950"
            aria-label={t('common.back')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <h1 className="text-3xl font-semibold tracking-normal">{copy.title}</h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-ink-600">{copy.subtitle}</p>
      </div>

      {mode === 'register' && (
        <div className="mt-8">
          <label className="mb-1.5 block text-sm font-medium text-ink-700">
            {t('auth.form.accountType')}
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {([
              { value: 'smb', label: t('auth.form.accountTypeSmb') },
              { value: 'agency', label: t('auth.form.accountTypeAgency') },
              { value: 'creator', label: t('auth.form.accountTypeCreator') },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setField('accountType', opt.value)}
                aria-pressed={formData.accountType === opt.value}
                className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                  formData.accountType === opt.value
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-input bg-white text-ink-600 hover:border-brand-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showOAuth && (
        <div className={cn('space-y-3', mode === 'register' ? 'mt-5' : 'mt-8')}>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={goGoogle}
            disabled={loading}
          >
            <GoogleIcon />
            {t('auth.continueGoogle')}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={goApple}
            disabled={loading}
          >
            <AppleIcon />
            {t('auth.continueApple')}
          </Button>
        </div>
      )}

      {showOAuth && (
        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-ink-400">
          <span className="h-px flex-1 bg-ink-950/10" />
          {t('auth.orEmail')}
          <span className="h-px flex-1 bg-ink-950/10" />
        </div>
      )}

      {error && (
        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mt-5 flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        {mode === 'register' && (
          <Input
            label={t('common.name')}
            type="text"
            placeholder={t('auth.form.fullNamePlaceholder')}
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            required
          />
        )}

        {['login', 'register', 'forgot-password'].includes(mode) && (
          <Input
            label={t('common.email')}
            type="email"
            placeholder={t('auth.form.emailPlaceholder')}
            value={formData.email}
            onChange={(e) => setField('email', e.target.value)}
            required
          />
        )}

        {(mode === 'login' || mode === 'register') && (
          <Input
            label={t('common.password')}
            type="password"
            placeholder="••••••••"
            value={formData.password}
            onChange={(e) => setField('password', e.target.value)}
            required
          />
        )}

        {mode === 'register' && (
          <PasswordStrengthIndicator password={formData.password} />
        )}

        {(mode === 'verify-email' || mode === 'verify-reset') && (
          <div className="py-3">
            <OTPInput
              value={formData.otp}
              onChange={(value) => setField('otp', value)}
              error={!!error}
              disabled={loading}
            />
          </div>
        )}

        {mode === 'reset-password' && (
          <>
            <Input
              label={t('auth.form.newPassword')}
              type="password"
              placeholder="••••••••"
              value={formData.newPassword}
              onChange={(e) => setField('newPassword', e.target.value)}
              required
            />
            <PasswordStrengthIndicator password={formData.newPassword} />
            <Input
              label={t('auth.form.confirmPassword')}
              type="password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={(e) => setField('confirmPassword', e.target.value)}
              required
            />
          </>
        )}

        {mode === 'login' && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setError('');
                setMode('forgot-password');
              }}
              className="text-sm font-semibold text-ink-500 transition hover:text-ink-950"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>
        )}

        <Button type="submit" variant="brand" className="w-full" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {copy.cta}
        </Button>
      </form>

      {mode === 'verify-email' && (
        <p className="mt-5 text-center text-sm text-ink-500">
          {t('auth.resend.question')}{' '}
          <button
            type="button"
            onClick={handleResendCode}
            disabled={loading || resendTimer > 0}
            className="font-semibold text-brand-600 transition hover:text-brand-700 disabled:opacity-50"
          >
            {resendTimer > 0
              ? t('auth.resend.timer', { seconds: resendTimer })
              : t('auth.resend.action')}
          </button>
        </p>
      )}

      {mode === 'verify-reset' && (
        <p className="mt-5 text-center text-sm text-ink-500">
          {t('auth.wrongEmail')}{' '}
          <button
            type="button"
            onClick={() => {
              setMode('forgot-password');
              setField('otp', '');
            }}
            className="font-semibold text-brand-600 transition hover:text-brand-700"
          >
            {t('auth.sendAgain')}
          </button>
        </p>
      )}

      {!compact && (mode === 'login' || mode === 'register') && (
        <p className="mt-7 text-center text-sm text-ink-500">
          {mode === 'login' ? t('auth.newTo') : t('auth.haveAccount')}{' '}
          <button
            type="button"
            onClick={() => {
              setError('');
              setSuccess('');
              setMode(mode === 'login' ? 'register' : 'login');
            }}
            className="font-semibold text-ink-950 transition hover:text-brand-600"
          >
            {mode === 'login' ? t('common.createAccount') : t('common.signIn')}
          </button>
        </p>
      )}
    </div>
  );
}
